import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation
} from "react-router-dom";
import Home from "./Home";
import Login from "./Login";
import Register from "./Register";
import ForgotPassword from "./ForgotPassword";
import FindMatches from "./FindMatches";
import CreateMatch from "./CreateMatch";
import TournamentList from "./TournamentList";
import CreateTournament from "./CreateTournament";
import MyBookings from "./MyBookings";
import Account from "./Account";
import Terms from "./Terms";
import Privacy from "./Privacy";
import ChatPage from "./ChatPage";
import NotFound from "./NotFound";
import WillPlayMotionLogo from "./components/WillPlayMotionLogo";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { getAuthMe, logoutAuth, parseAuthMeProfile } from "./api/authApi";
import { getPreferredApiBase } from "./api/client";
import { AuthUserContext } from "./auth/AuthUserContext";
import { clearStoredAuthSession } from "./auth/clientAuth";

const NAV_CAPTION = "Find.Host.Play&Win";
const WILLPLAY_PUSH_KEY = "willplayPushNotifications";

function formatRelativeTime(raw) {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

async function fetchNotifications() {
  const base = getPreferredApiBase().replace(/\/$/, "");
  const response = await fetch(`${base}/api/Notifications`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json", "X-WillPlay-Request": "1" }
  });
  const text = await response.text();
  let data = [];
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = [];
    }
  }
  return { response, data: Array.isArray(data) ? data : [] };
}

async function markNotificationRead(id) {
  if (!/^\d+$/.test(String(id))) return;
  const base = getPreferredApiBase().replace(/\/$/, "");
  await fetch(`${base}/api/Notifications/${id}/read`, {
    method: "PUT",
    credentials: "include",
    headers: { "X-WillPlay-Request": "1" }
  });
}

function NotificationBell() {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pushEnabled, setPushEnabled] = useState(() => {
    if (localStorage.getItem(WILLPLAY_PUSH_KEY) !== "1") return false;
    if (typeof Notification === "undefined") return false;
    return Notification.permission === "granted";
  });

  useEffect(() => {
    if (!open) return undefined;

    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { response, data } = await fetchNotifications();
        if (!alive || !response.ok) return;
        setItems(
          data.map((n) => ({
            id: String(n.id ?? n.Id),
            title: "Notification",
            body: String(n.message ?? n.Message ?? ""),
            read: Boolean(n.isRead ?? n.IsRead),
            at: formatRelativeTime(n.createdAt ?? n.CreatedAt)
          }))
        );
      } catch {
        /* ignore */
      }
    })();

    const base = getPreferredApiBase().replace(/\/$/, "");
    const conn = new HubConnectionBuilder()
      .withUrl(`${base}/notificationHub`, {
        withCredentials: true,
        headers: { "X-WillPlay-Request": "1" }
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    conn.on("ReceiveNotification", (message) => {
      setItems((prev) => [
        {
          id: `live-${Date.now()}`,
          title: "Notification",
          body: String(message || ""),
          read: false,
          at: "just now"
        },
        ...prev
      ]);
    });

    void (async () => {
      try {
        await conn.start();
        await conn.invoke("JoinNotificationGroup");
      } catch {
        /* ignore */
      }
    })();

    return () => {
      alive = false;
      void conn.stop();
    };
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const handlePushChange = async (e) => {
    const checked = e.target.checked;

    if (!checked) {
      setPushEnabled(false);
      localStorage.setItem(WILLPLAY_PUSH_KEY, "0");
      return;
    }

    if (typeof Notification === "undefined") {
      e.target.checked = false;
      return;
    }

    let perm = Notification.permission;

    if (perm === "default") {
      perm = await Notification.requestPermission();
    }

    if (perm === "granted") {
      setPushEnabled(true);
      localStorage.setItem(WILLPLAY_PUSH_KEY, "1");
      new Notification("WillPlay", { body: "Desktop alerts are enabled." });
    } else {
      setPushEnabled(false);
      localStorage.setItem(WILLPLAY_PUSH_KEY, "0");
      e.target.checked = false;
    }
  };

  return (
    <div className="notification-wrap" ref={rootRef}>
      <button
        type="button"
        className="notification-trigger ghost-btn small"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <svg
          className="notification-bell-icon"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
          />
        </svg>

        {unread > 0 ? (
          <span className="notification-badge">{unread > 9 ? "9+" : unread}</span>
        ) : null}
      </button>

      {open ? (
        <div className="notification-panel" role="dialog" aria-label="Notifications">
          <div className="notification-panel__head">
            <strong className="notification-panel__title">Notifications</strong>

            {unread > 0 ? (
              <button
                type="button"
                className="notification-markall"
                onClick={async () => {
                  setItems((list) => list.map((i) => ({ ...i, read: true })));
                  await Promise.all(
                    items
                      .filter((x) => !x.read && /^\d+$/.test(String(x.id)))
                      .map((x) => markNotificationRead(x.id))
                  );
                }}
              >
                Mark all read
              </button>
            ) : null}
          </div>

          <ul className="notification-list">
            {items.length === 0 ? (
              <li className="notification-item notification-item--empty">
                <p className="notification-item__body">No notifications yet.</p>
              </li>
            ) : (
              items.map((n) => (
                <li
                  key={n.id}
                  className={n.read ? "notification-item is-read" : "notification-item"}
                  onClick={() => {
                    if (!n.read) {
                      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
                      void markNotificationRead(n.id);
                    }
                  }}
                >
                  <div className="notification-item__title">{n.title}</div>
                  <p className="notification-item__body">{n.body}</p>
                  <div className="notification-item__meta">{n.at}</div>
                </li>
              ))
            )}
          </ul>

          <div className="notification-panel__foot">
            <label className="notification-toggle">
              <input
                type="checkbox"
                checked={pushEnabled}
                onChange={handlePushChange}
              />
              <span>Desktop alerts</span>
            </label>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function parseCaptionWords(raw) {
  return raw.split(".").flatMap((segment) => segment.split("&").filter(Boolean));
}

function captionSeparators(raw, wordCount) {
  const seps = [];
  const dotParts = raw.split(".");

  for (let i = 0; i < wordCount - 1; i++) {
    const chunk = dotParts[i] ?? "";

    if (i === wordCount - 2 && chunk.includes("&")) {
      seps.push("&");
    } else {
      seps.push(".");
    }
  }

  return seps;
}

function WordByWordCaption({ text }) {
  const words = useMemo(() => parseCaptionWords(text), [text]);

  const seps = useMemo(
    () => captionSeparators(text, words.length),
    [text, words.length]
  );

  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (words.length === 0) return undefined;

    if (reduceMotion) {
      setVisibleCount(words.length);
      return undefined;
    }

    const delayBeforeFirst = 400;
    const delayBetweenWords = 280;
    const delayAfterFull = 2600;

    const delay =
      visibleCount === 0
        ? delayBeforeFirst
        : visibleCount < words.length
          ? delayBetweenWords
          : delayAfterFull;

    const id = window.setTimeout(() => {
      if (visibleCount >= words.length) {
        setVisibleCount(0);
      } else {
        setVisibleCount((n) => n + 1);
      }
    }, delay);

    return () => window.clearTimeout(id);
  }, [reduceMotion, visibleCount, words.length]);

  const label = text.replace("&", " and ");

  return (
    <p
      className="nav-caption nav-caption--words"
      aria-label={label}
      aria-live="polite"
    >
      {words.slice(0, visibleCount).map((word, i) => (
        <React.Fragment key={`${word}-${i}`}>
          {i > 0 ? (
            <span className="nav-caption__sep" aria-hidden="true">
              {seps[i - 1] ?? "."}
            </span>
          ) : null}

          <span
            className={
              i === visibleCount - 1 && visibleCount > 0
                ? "nav-caption__word nav-caption__word--appear"
                : "nav-caption__word"
            }
          >
            {word}
          </span>
        </React.Fragment>
      ))}
    </p>
  );
}

function Navbar({ isLoggedIn, onLogout, profileImageUrl }) {
  const location = useLocation();

  if (
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/forgot-password"
  ) {
    return null;
  }

  return (
    <nav className="nav">
      <Link className="brand" to="/">
        <WillPlayMotionLogo size={32} animated className="brand-logo-motion" />
        <span>WillPlay</span>
      </Link>

      <WordByWordCaption text={NAV_CAPTION} />

      <div className="nav-actions">
        {isLoggedIn ? <NotificationBell /> : null}

        {isLoggedIn ? (
          <Link
            to="/account"
            className="account-nav-wrap ghost-btn small account-nav-link"
            aria-label="Account"
          >
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt=""
                className="nav-avatar"
                width={28}
                height={28}
              />
            ) : null}
            <span>Account</span>
          </Link>
        ) : null}

        <Link
          to={isLoggedIn ? "/chat" : "/login"}
          state={isLoggedIn ? undefined : { from: "/chat" }}
          className="ghost-btn small chat-nav-link"
          aria-label={isLoggedIn ? "Open messages" : "Sign in to open messages"}
        >
          <svg
            className="chat-nav-icon"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
            />
          </svg>
          <span className="chat-nav-text">Chat</span>
        </Link>

        {isLoggedIn && location.pathname === "/account" ? (
          <button
            type="button"
            className="ghost-btn"
            onClick={() => {
              void onLogout();
            }}
          >
            Logout
          </button>
        ) : !isLoggedIn ? (
          <>
            <a className="ghost-btn small auth-btn" href="/login">
              Login
            </a>
            <a className="primary-btn small auth-btn" href="/register">
              Register
            </a>
          </>
        ) : null}
      </div>
    </nav>
  );
}

function ProtectedRoute({ authReady, isLoggedIn, children }) {
  const location = useLocation();

  if (!authReady) {
    return (
      <main className="page">
        <p className="muted" style={{ padding: "1.5rem" }}>
          Checking your session…
        </p>
      </main>
    );
  }

  if (!isLoggedIn) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return children;
}

function AppLayout() {
  const [authReady, setAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { response, data } = await getAuthMe();
        if (cancelled) return;
        if (response.ok && data && typeof data === "object") {
          const profile = parseAuthMeProfile(data);
          if (profile) {
            setUserProfile(profile);
            setIsLoggedIn(true);
          } else {
            // If we can't parse a valid profile, treat as unauthenticated to avoid UI mismatches.
            setUserProfile(null);
            setIsLoggedIn(false);
          }
        } else {
          setUserProfile(null);
          setIsLoggedIn(false);
        }
      } catch {
        if (!cancelled) {
          setUserProfile(null);
          setIsLoggedIn(false);
        }
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onExpired = () => {
      setIsLoggedIn(false);
      setUserProfile(null);
    };

    window.addEventListener("willplay:session-expired", onExpired);

    return () => {
      window.removeEventListener("willplay:session-expired", onExpired);
    };
  }, []);

  useEffect(() => {
    const onRefresh = () => {
      void (async () => {
        try {
          const { response, data } = await getAuthMe();
          if (response.ok && data && typeof data === "object") {
            const profile = parseAuthMeProfile(data);
            if (profile) {
              setUserProfile(profile);
              setIsLoggedIn(true);
            }
          }
        } catch {
          /* ignore */
        }
      })();
    };

    window.addEventListener("willplay:auth-refresh", onRefresh);
    return () => window.removeEventListener("willplay:auth-refresh", onRefresh);
  }, []);

  const authApi = useMemo(
    () => ({
      onLoginSuccess(profile) {
        setIsLoggedIn(true);
        setUserProfile(profile);
      },

      async onLogout() {
        try {
          await logoutAuth();
        } catch {
          /* ignore */
        }
        clearStoredAuthSession();
        setUserProfile(null);
        setIsLoggedIn(false);
      }
    }),
    []
  );

  return (
    <div className="app-shell">
      <AuthUserContext.Provider value={userProfile}>
        <Navbar
          isLoggedIn={isLoggedIn}
          onLogout={authApi.onLogout}
          profileImageUrl={userProfile?.profileImageUrl ?? null}
        />

        <Routes>
        <Route path="/" element={<Home isLoggedIn={isLoggedIn} />} />

        <Route
          path="/login"
          element={
            isLoggedIn ? (
              <Navigate to="/" replace />
            ) : (
              <Login onLoginSuccess={authApi.onLoginSuccess} />
            )
          }
        />

        <Route
          path="/register"
          element={
            isLoggedIn ? (
              <Navigate to="/" replace />
            ) : (
              <Register onLoginSuccess={authApi.onLoginSuccess} />
            )
          }
        />
        <Route
          path="/forgot-password"
          element={
            isLoggedIn ? <Navigate to="/" replace /> : <ForgotPassword />
          }
        />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        <Route
          path="/chat"
          element={
            <ProtectedRoute authReady={authReady} isLoggedIn={isLoggedIn}>
              <ChatPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/matches"
          element={
            <ProtectedRoute authReady={authReady} isLoggedIn={isLoggedIn}>
              <FindMatches />
            </ProtectedRoute>
          }
        />

        <Route
          path="/create-match"
          element={
            <ProtectedRoute authReady={authReady} isLoggedIn={isLoggedIn}>
              <CreateMatch />
            </ProtectedRoute>
          }
        />

        <Route
          path="/tournaments"
          element={
            <ProtectedRoute authReady={authReady} isLoggedIn={isLoggedIn}>
              <TournamentList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/create-tournament"
          element={
            <ProtectedRoute authReady={authReady} isLoggedIn={isLoggedIn}>
              <CreateTournament />
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-bookings"
          element={
            <ProtectedRoute authReady={authReady} isLoggedIn={isLoggedIn}>
              <MyBookings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/account"
          element={
            <ProtectedRoute authReady={authReady} isLoggedIn={isLoggedIn}>
              <Account />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthUserContext.Provider>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}

export default App;